import {FC, useState} from 'react'
import {OrderItemModel} from '../models/OrderItemModel'
import {useIntl} from 'react-intl'
import {LazyLoadImage} from 'react-lazy-load-image-component'
import {Link} from 'react-router-dom'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {ERROR_ISSUES_DETECTED, ItemStatus, ITEM_ISSUES_DETECTED} from '../constants'
import {itemStatusClassMapping} from '../utils'

import {OrderModel} from '../models/OrderModel'
import './itemGrid.scss'
import ItemActionBox from './ItemActionBox'
import {useAppSelector} from '../../../../setup/redux/hooks'
import {selectSelectedItems, selectSelectedMode, setSelectedItem} from '../redux/OrderRedux'
import {useDispatch} from 'react-redux'
interface Props {
  items: OrderItemModel[]
  order: OrderModel
}

const ItemGrid: FC<Props> = (props) => {
  const items = props.items
  const intl = useIntl()
  const dispatch = useDispatch()
  const selectMode = useAppSelector(selectSelectedMode)
  const selectedItems = useAppSelector(selectSelectedItems)
  return (
    <div className='row g-6 g-xl-9 '>
      {items.map((item) => {
        const isDisabled = [ItemStatus.AI_PROCESSING, ItemStatus.PENDING].includes(item.status)
        const targetImage = item.outputImageUrl?.tiny
          ? item.outputImageUrl.tiny
          : item.inputImageUrl?.tiny
        const detectedIssues = item.note
          ? item.note
              .split(',')
              .map((note) => note.trim())
              .filter((note) => note && ITEM_ISSUES_DETECTED.includes(note))
          : []
        const hasErrorIssue = detectedIssues.some((issue) => ERROR_ISSUES_DETECTED.includes(issue))
        return (
          <div key={item.id} className='col-md-6 order-card col-xl-4'>
            <div className='card shadow border border-2 border-gray-300 border-hover'>
              <div className='border-0 order-item-card-title order-detail-item-card-title'>
                <Link to={'/orders/' + item.orderId + '/items/' + item.id}>
                  <div className='card-title m-0' style={{position: 'relative'}}>
                    <LazyLoadImage
                      src={targetImage ?? toAbsoluteUrl('/media/loading-bg.gif')}
                      alt={item.name}
                      placeholderSrc={toAbsoluteUrl('/media/loading-bg.gif')}
                      className='rounded-top'
                      effect='blur'
                      onError={(event: any) => {
                        if (event?.target?.src)
                          event.target.src = toAbsoluteUrl('/media/loading-bg.gif') // Replace with your default image URL
                      }}
                    />

                    {/* Issue icon for items with detected issues - danger color for blocking issues */}
                    {detectedIssues.length > 0 && (
                      <i
                        className={`bi ${
                          hasErrorIssue
                            ? 'bi-exclamation-octagon-fill text-danger'
                            : 'bi-exclamation-triangle-fill text-warning'
                        }`}
                        style={{
                          position: 'absolute',
                          top: '8px',
                          left: '8px',
                          fontSize: '1.2rem',
                          borderRadius: '50%',
                          padding: '4px',
                          cursor: 'help',
                          zIndex: 10,
                        }}
                        title={intl.formatMessage({id: 'ITEM.WARNING.PROCESSING_ISSUE'})}
                      ></i>
                    )}

                    {/* Warning badges for AI_PROCESSING items with notes - positioned at bottom left of image */}
                    {detectedIssues.length > 0 && (
                      <div
                        style={{
                          position: 'absolute',
                          bottom: '8px',
                          left: '8px',
                          display: 'flex',
                          flexDirection: 'row',
                          gap: '4px',
                          maxWidth: '200px',
                          zIndex: 10,
                        }}
                      >
                        {detectedIssues.map((noteText, index) => {
                          const hasErrorIssue = ERROR_ISSUES_DETECTED.includes(noteText.trim())
                          return (
                            <span
                              key={index}
                              className={`badge ${
                                hasErrorIssue ? 'badge-danger' : 'badge-warning'
                              } fw-bolder`}
                              style={{
                                fontSize: '0.7rem',
                                padding: '2px 6px',
                                backgroundColor: hasErrorIssue
                                  ? 'rgba(220, 53, 69, 0.9)'
                                  : 'rgba(255, 193, 7, 0.9)',
                                backdropFilter: 'blur(4px)',
                              }}
                            >
                              {intl.formatMessage({id: `ITEM.ISSUE.${noteText.trim()}`})}
                            </span>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </Link>
                {selectMode && (
                  <div className='card-toolbar card-selection-box'>
                    <div className='form-check form-check-sm form-check-custom form-check-solid'>
                      <input
                        className='form-check-input widget-9-check'
                        type='checkbox'
                        checked={selectedItems.includes(item.id)}
                        disabled={item.status !== ItemStatus.DONE}
                        value={item.id}
                        onChange={() => {
                          dispatch(
                            setSelectedItem({id: item.id, isAdd: !selectedItems.includes(item.id)})
                          )
                        }}
                      />
                    </div>
                  </div>
                )}
                <div className='card-toolbar card-order-status'>
                  <span
                    className={`badge ${itemStatusClassMapping(
                      item.status
                    )} fw-bolder me-auto px-4 py-3`}
                  >
                    {intl.formatMessage({id: 'Item.status.' + item.status})}
                  </span>
                </div>
              </div>
              <div className='card-body p-3 '>
                <div className='d-flex justify-content-between mb-3'>
                  <div className='fs-3 fw-bolder text-dark order-card-title'>
                    <Link
                      className='text-dark'
                      to={'/orders/' + item.orderId + '/items/' + item.id}
                    >
                      {item.name}
                    </Link>
                  </div>
                  <div className='card-order-actions'>
                    <ItemActionBox item={item} />
                  </div>
                </div>

                <div className='d-flex flex-wrap justify-content-between'>
                  <div className='border border-gray-300 border-dashed rounded py-3 px-4 '>
                    <div className='fs-6 text-gray-800 fw-bolder'>{item.price}</div>
                    <div className='fw-bold text-gray-400'>
                      {intl.formatMessage({id: 'Item.Credit'})}
                    </div>
                  </div>
                  <div className='border border-gray-300 border-dashed rounded  py-3 px-4 '>
                    <div className='fs-6 text-gray-800 fw-bolder'>{props.order.setting?.name}</div>
                    <div className='fw-bold text-gray-400'>
                      {intl.formatMessage({id: 'Item.setting'})}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {isDisabled && (
              <div className='order-card-disable d-flex justify-content-center'>
                <div className='spinner-grow m-auto text-warning' role='status'>
                  <span className='sr-only'>Loading...</span>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default ItemGrid
